import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { CognitoUser } from '@aws-amplify/auth';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AuthStateFacade, MaintenanceWorkFacade } from '@zero/shared/util-global-state';
import { Customers, IBackendResponse, IUser } from '@zero/shared/util-models';
import { AuthState } from '@zero/shared/util-services';
import { CognitoRefreshToken, CognitoUserSession } from 'amazon-cognito-identity-js';
import { Auth } from 'aws-amplify';
import { durationUntilLogout } from 'constants/shared-constants';
import { commonRoutes } from 'constants/web-app-constants';
import { environment } from 'environments/environment';
import { CustomerInfrastructure } from 'libs/shared/util-global-state/src/lib/state/customer/customer.infrastructure';
import { firstValueFrom, from, Observable, of, Subject } from 'rxjs';
import { catchError, filter, map, switchMap, take } from 'rxjs/operators';
import { CustomAmplifyService } from '../amplify/custom-amplify.service';
import { CrossTabsEventsService } from '../dom/cross-tabs-events.service';
import { WindowService } from '../dom/window.service';
import { HttpErrorHandler } from '../http/http-error-handler.service';
import { UserMessagesHolderService } from '../user-messages/user-messages-holder.service';
import { AuthService, IAuthentication } from './auth.service';

@Injectable()
export class AmplifyAuthService extends AuthService {
  private _amplifyService: CustomAmplifyService;
  private readonly _COGNITO_ATTR_PHONE = 'phone_number';
  private _messageService: UserMessagesHolderService;
  private _crossTabsEventsService: CrossTabsEventsService;
  private _maintenanceWorkFacade: MaintenanceWorkFacade;
  private _authStateFacade: AuthStateFacade;
  private readonly _modalService = inject(NgbModal);
  private readonly _custormerInfrastuc = inject(CustomerInfrastructure);

  private static extractLastLoginFromCognitoUser(user: CognitoUser): number {
    if (user.getSignInUserSession()?.getIdToken()?.payload?.['auth_time']) {
      return user.getSignInUserSession().getIdToken().payload['auth_time'] * 1000;
    } else {
      return undefined;
    }
  }

  constructor(http: HttpClient, httpErrorHandler: HttpErrorHandler, router: Router) {
    super(http, httpErrorHandler, router);
    this._messageService = inject(UserMessagesHolderService);
    this._crossTabsEventsService = inject(CrossTabsEventsService);
    this._maintenanceWorkFacade = inject(MaintenanceWorkFacade);
    this._authStateFacade = inject(AuthStateFacade);
    this.isLoggedIn$()
      .pipe(
        switchMap((loggedIn) => {
          if (loggedIn) {
            const customerData = this.getCurrentCustomerData();
            customerData && this._authStateFacade.setCustomerId(Object.keys(customerData).find((key) => customerData[key]?.owner === true));
            return this._crossTabsEventsService.getLogoutInDifferentTab();
          } else {
            return of(undefined);
          }
        }),
      )
      .subscribe(async (differentTabEvent) => {
        if (differentTabEvent?.loggedout) {
          await this.logout(
            false,
            true,
            'Du hast Dich in einem anderen Tab / Fenster ausgeloggt. ' +
              'Wenn Du Dich in einem anderen Fenster bereits wieder eingeloggt hast, dann lade einfach diese Seite neu.',
            { reason: 'logout in different tab' },
          );
        } else if (differentTabEvent?.userchanged) {
          const win = inject(WindowService).getWindow();
          win.location.reload();
        }
      });

    const remainingTimeToForcedLogoutInterval = setInterval(async () => {
      await this.broadcastRemainingTimeToForcedLogout();
    }, 10000);

    this._maintenanceWorkFacade.maintenanceActive$.subscribe((isActive) => isActive && clearInterval(remainingTimeToForcedLogoutInterval));

    this.getUserSession()
      .pipe(filter((session) => !!session))
      .subscribe((session) => {
        this._authStateFacade.setAuthToken(session.getAccessToken().getJwtToken());
        this._authStateFacade.setUserName(session.getIdToken().payload['cognito:username']);
      });

    this.clearPushNotifications$ = new Subject<boolean>();
  }

  public createAmplifyEventsMappedObservable(): Observable<IAuthentication> {
    this._amplifyService = inject(CustomAmplifyService);
    this._amplifyService.configure();
    return this._amplifyService.authStateChange$.pipe(
      map((authState: AuthState) => {
        const returnedAuth: IAuthentication = {
          authState: authState,
          user: undefined,
        };

        if (authState && authState.state === 'signedIn') {
          returnedAuth.user = {
            lastLogin: AmplifyAuthService.extractLastLoginFromCognitoUser(authState.user),
            userName: `${authState.user.username}`,
            customers: this.extractCustomerDataFromCognitoUser(authState.user),
            logoutTime: null,
            verifiedPhoneNumber: this.extractVerifiedPhoneNumberFromCognitoUser(authState.user),
          } as IUser;
        }
        return returnedAuth;
      }),
    );
  }

  private extractCustomerDataFromCognitoUser(user: CognitoUser): Customers {
    if (user.getSignInUserSession()?.getIdToken()?.payload?.['prm']) {
      const permissionData = JSON.parse(user.getSignInUserSession().getIdToken().payload['prm']);
      // enhance customer object with id
      Object.keys(permissionData).forEach((custId) => {
        permissionData[custId].custId = custId;
        permissionData[custId].dep = this._custormerInfrastuc.removeK(permissionData[custId].dep);
      });

      return permissionData;
    } else {
      return {};
    }
  }

  public login(userName: string, password: string): Observable<IBackendResponse<any>> {
    if (!userName || !password) {
      return of({
        success: false,
        message: 'Bitte gib Benutzername und Passwort ein. ',
      });
    }

    return this.isLoggedIn$().pipe(
      take(1),
      switchMap((loggedIn) => {
        if (loggedIn) {
          return of({
            success: false,
            message: 'Du bist bereits eingeloggt.',
          });
        } else {
          return from(Auth.signIn(userName, password)).pipe(
            map(() => ({ success: true }) as IBackendResponse<any>),
            catchError((err) => {
              const retVal = { success: false } as IBackendResponse<any>;
              if (err.code && err.code === 'PasswordResetRequiredException') {
                this.loginEvents$.next({ type: 'passwordResetRequired' });
                this.router.navigate([commonRoutes.prelogin.forgot_pwd]);
              } else if (err.code && (err.code === 'NotAuthorizedException' || err.code === 'UserNotFoundException')) {
                if (err.message === 'User is disabled') {
                  this.loginEvents$.next({ type: 'userDisabled' });
                  retVal.message = 'Die Anmeldung ist fehlgeschlagen, weil der Benutzer deaktiviert ist.';
                } else {
                  this.loginEvents$.next({ type: 'wrongUsernameOrPassword' });
                  retVal.message = 'Der eingegebene Benutzername oder das Passwort ist falsch.';
                }
              } else {
                this.loginEvents$.next({ type: 'loginFailed' });
                retVal.message = 'Die Anmeldung ist fehlgeschlagen.';
                if (err.message && err.message.length > 0) {
                  retVal.message = err.message;
                }
              }
              return of(retVal);
            }),
          );
        }
      }),
    );
  }

  public logout$(serverLogInfo?): Observable<boolean> {
    return new Observable<boolean>((observer) => {
      const logOutUrl = `${environment.apihost}/api/auth/logout`;
      this.http.post(logOutUrl, serverLogInfo ?? null).subscribe(() => {
        this.currentAuth = null;
        this._amplifyService
          .auth()
          .signOut()
          .then(() => {
            observer.next(true);
            observer.complete();
          });
      });
    });
  }

  private globalLogout = async (serverLogInfo?: unknown): Promise<void> => {
    await firstValueFrom(this.http.post<unknown>(`${environment.apihost}/api/user/globalsignout`, serverLogInfo ?? null));
    await this._amplifyService.auth().signOut();
    this.clearCurrentAuth();
    this._authStateFacade.logoutUser();
  };

  private defaultLogout = async (serverLogInfo?: unknown): Promise<void> => {
    await firstValueFrom(this.http.post<unknown>(`${environment.apihost}/api/auth/logout`, serverLogInfo ?? null));
    this.clearCurrentAuth();
    await this._amplifyService.auth().signOut();
    this._authStateFacade.logoutUser();
  };

  public async logout(
    global?: boolean,
    reload?: boolean,
    messageAfterLogout?: string,
    serverLogInfo?: unknown,
    cb?: () => void,
  ): Promise<void> {
    if (global === true) {
      await this.globalLogout(serverLogInfo);
    } else {
      await this.defaultLogout(serverLogInfo);
      if (reload === true) {
        this.redirectUrl = undefined;
        if (!this.router.isActive('/', true)) {
          this.clearUserMessages();
          this.clearPushNotifications();
          this.clearPopups();
          this.router.navigate([commonRoutes.prelogin.home]).then(() => {
            if (messageAfterLogout) {
              this._messageService.addErrorMessage(messageAfterLogout);
            }
          });
          return;
        }
      }
    }

    if (cb) {
      cb();
    }
  }

  private clearUserMessages = (): void => this._messageService.clearAllMessages();
  private clearPushNotifications = (): void => this.clearPushNotifications$.next(true);
  private clearPopups = (): void => this._modalService.dismissAll();

  public getAuthToken(): Observable<string> {
    return this.getUserSession().pipe(
      map((session: CognitoUserSession) => {
        let retToken = null;
        if (session?.getIdToken()) {
          retToken = session.getIdToken().getJwtToken();
        }
        return retToken;
      }),
    );
  }

  public getRefreshToken(): Observable<string> {
    return this.getUserSession().pipe(
      map((session: CognitoUserSession) => {
        let retToken = null;
        if (session?.getRefreshToken()) {
          retToken = session.getRefreshToken().getToken();
        }
        return retToken;
      }),
    );
  }

  public getAccessToken(): Observable<string> {
    return this.getUserSession().pipe(
      map((session: CognitoUserSession) => {
        let retToken = null;
        if (session?.getAccessToken()) {
          retToken = session.getAccessToken().getJwtToken();
        }
        return retToken;
      }),
    );
  }

  public getCurrentUserName(): string {
    if (this.currentAuth?.user?.userName) {
      return this.currentAuth.user.userName;
    } else {
      return '';
    }
  }

  public getCurrentVerifiedPhoneNumber(): string | undefined {
    if (this.currentAuth?.user?.verifiedPhoneNumber) {
      return this.currentAuth.user.verifiedPhoneNumber;
    } else {
      return undefined;
    }
  }

  public getCurrentCustomerData(): Customers {
    if (this.currentAuth?.user) {
      return this.currentAuth.user.customers;
    } else {
      return {};
    }
  }

  protected getUserSession(): Observable<CognitoUserSession> {
    return from(Auth.currentSession()).pipe(
      switchMap((cus: CognitoUserSession) => {
        if (cus) {
          return this._amplifyService
            .getStorage()
            .getCookieSaved$()
            .pipe(
              filter((cookieSaved) => cookieSaved === true),
              map(() => cus),
              take(1),
            );
        } else {
          return of(null);
        }
      }),
      catchError(async () => {
        if (this.currentAuth?.authState?.state === 'signedIn') {
          await this.logout(false, false, undefined, {
            reason: 'could not get Auth.currentSession',
          });
          this.loginEvents$.next({ type: 'signedOut' });
        }

        return null;
      }),
    );
  }

  protected getUser(): Observable<CognitoUser> {
    return from(Auth.currentAuthenticatedUser()).pipe(catchError(() => of(null)));
  }

  public setNewPassword(inputNewPassword: string): Observable<IBackendResponse<any>> {
    const user = this.currentAuth.authState.user;
    return from(Auth.completeNewPassword(user, inputNewPassword, [])).pipe(
      map(() => {
        this.loginEvents$.next({ type: 'passwordResetSuccessful' });
        return {
          success: true,
          message: 'Dein Passwort wurde zurückgesetzt, bitte melde Dich mit Deinem neuen Passwort an.',
        } as IBackendResponse<any>;
      }),
      catchError((err) => {
        const retVal: IBackendResponse<any> = { success: false };
        if (err.message) {
          retVal.message = err.message;
        }
        return of(retVal);
      }),
    );
  }

  public setPasswordAfterForgotten(username: string, verificationCode: string, password: string): Observable<IBackendResponse<any>> {
    return from(Auth.forgotPasswordSubmit(username, verificationCode, password)).pipe(
      map(() => {
        this.loginEvents$.next({ type: 'passwordResetSuccessful' });
        return {
          success: true,
          message: 'Dein Passwort wurde zurückgesetzt, bitte melde Dich mit Deinem neuen Passwort an.',
        };
      }),
      catchError((err) => {
        const retVal: IBackendResponse<any> = { success: false };
        if (err.code && err.code === 'ExpiredCodeException') {
          this.loginEvents$.next({ type: 'passwordResetCodeExpired' });
          retVal.message =
            'Der Verifikationscode für die Passwortrücksetzung ist abgelaufen. Bitte fordere einen neuen Verifikationscode an.';
        } else if (err.code && err.code === 'CodeMismatchException') {
          this.loginEvents$.next({ type: 'passwordResetCodeWrong' });
          retVal.message = 'Der eingegebene Verifikationscode für die Passwortrücksetzung ist falsch.';
        } else {
          this.loginEvents$.next({ type: 'passwordResetError' });
          retVal.message = 'Aufgrund eines Fehlers konnte Dein Passwort nicht zurückgesetzt werden.';
        }
        return of(retVal);
      }),
    );
  }

  public forgotPassword(username: string): Observable<boolean> {
    return from(
      Auth.forgotPassword(username)
        .then(() => {
          this.loginEvents$.next({ type: 'forgotPassword' });
          return true;
        })
        .catch(() => false),
    );
  }

  public sendMFACode(mfaCode: string): Observable<IBackendResponse<any>> {
    return from(Auth.confirmSignIn(this.currentAuth.authState.user, mfaCode, 'SOFTWARE_TOKEN_MFA')).pipe(
      map((result) => {
        this._amplifyService.setAuthState({ state: 'signedIn', user: result });
        return { success: true };
      }),
      catchError((err) => {
        const retVal: IBackendResponse<any> = { success: false };
        if (err.code === 'CodeMismatchException') {
          retVal.message = 'Der eingegebene Code ist falsch.';
        } else if (err.message) {
          retVal.message = err.message;
        }
        return of(retVal);
      }),
    );
  }

  private async broadcastRemainingTimeToForcedLogout() {
    if (this.currentAuth?.user?.lastLogin) {
      if (this.currentAuth.user.logoutTime) {
        await this.broadcastLogoutTime();
        return;
      }
      this.http.get(`${environment.apihost}/api/user/logouttime`).subscribe({
        next: async (result: number) => {
          this.currentAuth.user.logoutTime = result;
          if (result > this.currentAuth.user.lastLogin + durationUntilLogout) {
            this.currentAuth.user.logoutTime = this.currentAuth.user.lastLogin + durationUntilLogout;
          }
          await this.broadcastLogoutTime();
        },
      });
    }
  }

  private async broadcastLogoutTime(): Promise<void> {
    const logoutTime = this.currentAuth.user.logoutTime;
    if (!logoutTime) {
      return;
    }

    const dateNow = Date.now();
    const remainingTime = logoutTime - dateNow;
    if (remainingTime < 0) {
      await this.logout(null, true, undefined, {
        reason: 'remainingTime < 0',
        logoutTime,
        remainingTime,
        dateNow,
      });
    }
    this.remainingTimeToForcedLogout$.next(remainingTime);
  }

  public verifyMobileNrForCurrentUser(verificationCode: string): Promise<boolean> {
    return this._amplifyService
      .auth()
      .verifyCurrentUserAttributeSubmit(this._COGNITO_ATTR_PHONE, verificationCode)
      .then(() => true)
      .catch(() => false);
  }

  public initiateMobileNrVerificationForCurrentUser(): Promise<void> {
    return this._amplifyService
      .auth()
      .verifyCurrentUserAttribute(this._COGNITO_ATTR_PHONE)
      .then(() => {
        // nothing to do
      });
  }

  public refreshSession(): Observable<void> {
    return this.getRefreshToken().pipe(
      map((token) => {
        this._amplifyService
          .auth()
          .currentAuthenticatedUser()
          .then((cu: CognitoUser) => {
            cu.refreshSession(new CognitoRefreshToken({ RefreshToken: token }), () => true);
          });
      }),
    );
  }

  public logoutMaintenance() {
    this._amplifyService
      .auth()
      .signOut()
      .then(() => {
        this.clearCurrentAuth();
        this._authStateFacade.logoutUser();
        this.router.navigate(['/']);
      });
  }

  private extractVerifiedPhoneNumberFromCognitoUser(user: CognitoUser): string | undefined {
    if (user.getSignInUserSession()?.getIdToken()?.payload?.['phone_number_verified'] === true) {
      return user.getSignInUserSession().getIdToken().payload['phone_number'];
    } else {
      return undefined;
    }
  }
}
